/**
 * Company notification defaults (SAD-954): what every person in the company
 * gets until they choose otherwise in Settings → Notifications. Shown only to
 * admin, internal and the Company Settings role (useTenantAreas); ryugu
 * re-checks every call.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, SpecraInlineBanner, SpecraSpinner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { NotificationPreferencesTable } from '../../../components/notifications/NotificationPreferencesTable';
import { useCompanyNotificationDefaults, useUpdateCompanyNotificationDefaults } from '../../../components/notifications/preferencesHooks';
import { useAuth } from '../../../contexts/authContext';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
export function CompanyNotificationDefaults() {
    const { user } = useAuth();
    const companyId = user?.tenant_id ?? '';
    const { canManageCompanySettings } = useTenantAreas();
    const { data: kinds, isLoading, error } = useCompanyNotificationDefaults(companyId, canManageCompanySettings);
    const update = useUpdateCompanyNotificationDefaults(companyId);
    if (!canManageCompanySettings) return null;
    const onUpdate = (u)=>update.mutate(u, {
            onSuccess: ()=>toast.success('Company defaults saved'),
            onError: (e)=>toast.error(e instanceof Error ? e.message : 'Could not save')
        });
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.companyNotificationDefaults.section,
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                children: [
                    /*#__PURE__*/ _jsxs(CardTitle, {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(BellRing, {
                                className: "h-4 w-4"
                            }),
                            " Company notification defaults"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CardDescription, {
                        children: "What everyone in the company gets until they change it in their own Settings. Their own choice always wins."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    isLoading && /*#__PURE__*/ _jsx(SpecraSpinner, {}),
                    error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        title: "Could not load company defaults",
                        children: error instanceof Error ? error.message : String(error)
                    }),
                    kinds && /*#__PURE__*/ _jsx(NotificationPreferencesTable, {
                        kinds: kinds,
                        ownSource: "COMPANY",
                        onUpdate: onUpdate,
                        pending: update.isPending
                    })
                ]
            })
        ]
    });
}
